// ============================================================
// Origini fidate del token studente, lette A RUNTIME per ambiente (WP-13f, passo preliminare)
// ============================================================
// Il collaboration-service di ogni ambiente (container locali, staging, produzione) serve
//   GET <COLLAB_API_BASE>/runtime-config/trusted-origins
//   -> { "appOrigins": [...], "parentOrigins": [...] }
// letto dalle sue variabili d'ambiente. Cosi' la STESSA immagine (anche quella di produzione aperta dall'IP
// del server) funziona dove l'origine e' nell'elenco di quell'ambiente, senza ricompilare nulla.
//   - appOrigins:    origini delle app incorporate a cui TechyWeb (parent) puo' rispondere;
//   - parentOrigins: origini di TechyWeb da cui un'app (figlio) accetta il token, oltre a *.techlab4kids.info.
// L'elenco arriva dal backend che l'app gia' usa (stessa origine con /api-collab, oppure l'URL configurato):
// un sito che incornicia l'app non puo' farlo dire cose diverse.
//
// Chiuso per difetto: elenco assente, vuoto, non raggiungibile, risposta malformata = nessuna origine
// aggiunta. Un solo console.warn per motivo, mai con il token. I divieti (storage.*, user-content, ...)
// restano nel codice (originRules) e valgono anche per le voci dell'elenco.
// ============================================================

import { parseTrustedOrigins, warnOnce } from "./originRules";

export const TRUSTED_ORIGINS_RUNTIME_PATH = "/runtime-config/trusted-origins";

export interface RuntimeTrustedOrigins {
  appOrigins: string[];
  parentOrigins: string[];
}

export interface LoadRuntimeTrustedOriginsOptions {
  /** Base del collaboration-service (di norma COLLAB_API_BASE). */
  apiBase: string;
  /** Solo test. */
  fetchImpl?: typeof fetch;
  /** Attesa massima della risposta. Default 1500 ms. */
  timeoutMs?: number;
}

/** Valida e normalizza il corpo della risposta; null se non e' nella forma attesa. */
export function parseRuntimeTrustedOrigins(payload: unknown): RuntimeTrustedOrigins | null {
  if (typeof payload !== "object" || payload === null) return null;
  const p = payload as { appOrigins?: unknown; parentOrigins?: unknown };
  const list = (v: unknown): string[] | null => (Array.isArray(v) ? parseTrustedOrigins(v as string[]) : null);
  const appOrigins = list(p.appOrigins);
  const parentOrigins = list(p.parentOrigins);
  if (appOrigins === null || parentOrigins === null) return null;
  return { appOrigins, parentOrigins };
}

const RETRY_AFTER_FAILURE_MS = 30_000;
type CacheEntry = { promise: Promise<RuntimeTrustedOrigins | null>; failedAt: number | null };
const cache = new Map<string, CacheEntry>();

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/, "")}${path}`;
}

/**
 * Scarica l'elenco una volta sola (promessa in cache per base). Un fallimento resta in cache 30 s e poi si
 * ritenta alla richiesta successiva: TechyWeb resta aperto ore e non deve restare chiuso per un avvio
 * sfortunato del backend. Ritorna null se non disponibile (chiuso per difetto).
 */
export function loadRuntimeTrustedOrigins(options: LoadRuntimeTrustedOriginsOptions): Promise<RuntimeTrustedOrigins | null> {
  const key = options.apiBase;
  const hit = cache.get(key);
  if (hit && (hit.failedAt === null || Date.now() - hit.failedAt < RETRY_AFTER_FAILURE_MS)) return hit.promise;

  const entry: CacheEntry = { promise: Promise.resolve(null), failedAt: null };
  entry.promise = fetchOnce(options).then((r) => {
    if (r === null) entry.failedAt = Date.now();
    return r;
  });
  cache.set(key, entry);
  return entry.promise;
}

async function fetchOnce(options: LoadRuntimeTrustedOriginsOptions): Promise<RuntimeTrustedOrigins | null> {
  const doFetch = options.fetchImpl ?? (typeof fetch === "function" ? fetch : undefined);
  if (!doFetch) return null;
  const timeoutMs = options.timeoutMs ?? 1500;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await doFetch(joinUrl(options.apiBase, TRUSTED_ORIGINS_RUNTIME_PATH), {
      method: "GET",
      // Elenco pubblico e non segreto: niente cookie ne' header di identita' (e quindi niente preflight).
      credentials: "omit",
      cache: "no-store",
      signal: controller?.signal,
    });
    if (!response.ok) {
      warnOnce("runtime-list", `[studentToken] elenco origini fidate non disponibile (HTTP ${response.status}): nessuna origine aggiunta`);
      return null;
    }
    const parsed = parseRuntimeTrustedOrigins(await response.json());
    if (!parsed) {
      warnOnce("runtime-list", "[studentToken] elenco origini fidate malformato: nessuna origine aggiunta");
      return null;
    }
    if (parsed.appOrigins.length === 0 && parsed.parentOrigins.length === 0) {
      warnOnce("runtime-list", "[studentToken] elenco origini fidate vuoto: nessuna origine aggiunta");
    }
    return parsed;
  } catch {
    warnOnce("runtime-list", "[studentToken] elenco origini fidate non raggiungibile: nessuna origine aggiunta");
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Solo test. */
export function __resetRuntimeTrustedOriginsForTests(): void {
  cache.clear();
}
